/**
 * 地图配置文件
 * 定义离线地图瓦片服务配置和图层参数
 */

// 离线瓦片服务基础URL
export const OFFLINE_MAP_BASE_URL = 'https://fairway-map.oss-cn-guangzhou.aliyuncs.com/map';

// 备用URL
export const OFFLINE_MAP_BACKUP_URL = 'http://map.fairway.org.cn/map';

// 天地图Key
export const TIANDITU_KEY = '038937b9b91fd28203af58e3dd9b1396';

// 地图模式枚举
export const MAP_MODE = {
  TIANDITU: 'tianditu',   // 天地图在线模式
  OFFLINE: 'offline'       // 离线地图模式
};

// 离线图层类型枚举
export const OFFLINE_LAYER_TYPE = {
  ROAD: 'road',           // 道路图
  SEA: 'sea',             // 海图
  STAR: 'star',           // 卫星图
  STAR_LABEL: 'star_label' // 卫星文字标注
};

// 离线图层配置
export const OFFLINE_LAYER_CONFIGS = {
  road: {
    name: '道路图',
    type: 'road',
    format: 'png',
    color: '#e74c3c',
    opacity: 1.0,
    swapXY: false,        // 正常坐标顺序
    minZoom: 1,
    maxZoom: 18
  },
  sea: {
    name: '海图',
    type: 'sea',
    format: 'png',
    color: '#3498db',
    opacity: 1.0,         // 海图瓦片自带透明度，不需要额外设置
    swapXY: true,         // ⚠️ 关键：x和y坐标对调
    minZoom: 1,
    maxZoom: 18
  },
  star: {
    name: '卫星图',
    type: 'star',
    format: 'jpg',
    color: '#2ecc71',
    opacity: 1.0,
    swapXY: false,
    minZoom: 1,
    maxZoom: 18
  },
  star_label: {
    name: '卫星文字标注',
    type: 'star',
    format: 'png',
    color: '#f39c12',
    opacity: 1.0,
    swapXY: false,
    minZoom: 1,
    maxZoom: 15,          // ⚠️ 关键：仅1-15级显示
    isLabel: true
  }
};

/**
 * 生成离线瓦片URL模板
 * @param {string} layerType - 图层类型 (road/sea/star/star_label)
 * @param {string} baseUrl - 基础URL，默认使用阿里云OSS
 * @returns {string} URL模板
 */
export function getOfflineTileUrlTemplate(layerType, baseUrl = OFFLINE_MAP_BASE_URL) {
  const config = OFFLINE_LAYER_CONFIGS[layerType];
  if (!config) {
    console.error(`未知的离线图层类型: ${layerType}`);
    return '';
  }
  
  // 海图坐标对调：/{z}/{y}/{x}
  // 其他图层正常：/{z}/{x}/{y}
  if (config.swapXY) {
    return `${baseUrl}/${config.type}/{z}/{y}/{x}.${config.format}`;
  } else {
    return `${baseUrl}/${config.type}/{z}/{x}/{y}.${config.format}`;
  }
}

/**
 * 创建vue2-leaflet图层配置对象
 * @param {string} layerType - 图层类型
 * @param {string} layerRole - 图层角色 ('base' | 'overlay')
 * @param {Object} extraOptions - 额外选项
 * @returns {Object} 图层配置对象
 */
export function createOfflineLayerConfig(layerType, layerRole = 'base', extraOptions = {}) {
  const config = OFFLINE_LAYER_CONFIGS[layerType];
  if (!config) {
    console.error(`未知的离线图层类型: ${layerType}`);
    return null;
  }
  
  return {
    name: `offline-${layerType}`,
    url: getOfflineTileUrlTemplate(layerType),
    attribution: config.name,
    subdomains: [],
    layerType: layerRole,
    visible: true,
    options: {
      minZoom: config.minZoom,
      maxZoom: config.maxZoom,
      opacity: extraOptions.opacity !== undefined ? extraOptions.opacity : config.opacity,
      ...extraOptions
    }
  };
}

/**
 * 获取默认离线地图图层组合
 * 基础图层：卫星图
 * 叠加图层：海图 + 卫星文字标注
 * @returns {Array} 图层配置数组
 */
export function getDefaultOfflineLayers() {
  return [
    // 基础图层：卫星图 (JPG)
    createOfflineLayerConfig('star', 'base', { opacity: 1.0 }),
    // 叠加图层：海图 (PNG) - 注意坐标对调，海图瓦片自带透明度
    createOfflineLayerConfig('sea', 'overlay', { opacity: 1.0 }),
    // 叠加图层：卫星文字标注 (PNG) - 仅1-15级
    createOfflineLayerConfig('star_label', 'overlay', { opacity: 1.0 })
  ];
}

/**
 * 地图模式存储键名
 */
export const MAP_MODE_STORAGE_KEY = 'mapMode';

/**
 * 获取当前地图模式
 * @returns {string} 当前地图模式
 */
export function getCurrentMapMode() {
  try {
    return localStorage.getItem(MAP_MODE_STORAGE_KEY) || MAP_MODE.TIANDITU;
  } catch (error) {
    console.error('获取地图模式失败:', error);
    return MAP_MODE.TIANDITU;
  }
}

/**
 * 设置地图模式
 * @param {string} mode - 地图模式
 */
export function setMapMode(mode) {
  try {
    if (mode === MAP_MODE.TIANDITU || mode === MAP_MODE.OFFLINE) {
      localStorage.setItem(MAP_MODE_STORAGE_KEY, mode);
      return true;
    }
    console.error('无效的地图模式:', mode);
    return false;
  } catch (error) {
    console.error('设置地图模式失败:', error);
    return false;
  }
}

/**
 * 切换地图模式
 * @returns {string} 切换后的地图模式
 */
export function toggleMapMode() {
  const currentMode = getCurrentMapMode();
  const newMode = currentMode === MAP_MODE.TIANDITU ? MAP_MODE.OFFLINE : MAP_MODE.TIANDITU;
  setMapMode(newMode);
  return newMode;
}

/**
 * 判断当前是否为离线地图模式
 * @returns {boolean}
 */
export function isOfflineMode() {
  return getCurrentMapMode() === MAP_MODE.OFFLINE;
}

